// What changed while nobody was looking.
//
// A dispatcher is not watching the board. They are on the phone, or in
// the yard, or dealing with the thing that went wrong twenty minutes
// ago. Before this, a stop failing at 14:32 re-sorted a list in silence
// and the console's careful honesty about what a run said reached
// precisely nobody.
//
// So: a log, not a toast. A toast is for somebody already looking at the
// screen — it fires, it fades, and if they were not there it may as well
// not have happened. What a dispatcher coming back to the desk needs is
// the list of what they missed, with the times on it.
//
// Every line here is a **transition** between two snapshots of the same
// run, and every line carries the run's own timestamp rather than the
// moment this console noticed. Nothing is invented: if a record does not
// say a door was dealt with, no line appears for it.

import { useEffect, useRef, useState } from "react";
import { STOP_OUTCOME, THREAD_STATE, type FollowSnapshot } from "@wayfind/core";
import type { StoredJob } from "./state";

export type EventTone = "settled" | "halt" | "quiet" | "live";

export interface RunEvent {
  id: string;
  jobId: string;
  /** The run's own record time where there is one, else when noticed. */
  atUnixSeconds: number;
  who: string;
  what: string;
  tone: EventTone;
}

/** Only the parts of a snapshot a transition can be read from. */
interface Seen {
  outcomes: number[];
  stale: boolean;
  ended: boolean;
  ever: boolean;
}

function seenOf(snapshot: FollowSnapshot | null): Seen | null {
  if (!snapshot) return null;
  const { status } = snapshot;
  return {
    outcomes: status.outcomes.slice(),
    stale: status.observed.stale,
    ended: status.state === THREAD_STATE.COMPLETED || status.state === THREAD_STATE.CANCELED,
    ever: status.observed.ever
  };
}

/** The most lines to keep. A dispatcher scrolling a day is reading History. */
const KEEP = 40;

/**
 * Watch the live snapshots and turn changes into lines.
 *
 * The first sight of a run seeds the baseline without emitting anything —
 * otherwise opening the console would announce every door already dealt
 * with today as if it had just happened.
 */
export function useRunEvents(
  jobs: StoredJob[],
  live: Map<string, FollowSnapshot>,
  nameFor: (job: StoredJob) => string
): RunEvent[] {
  const [events, setEvents] = useState<RunEvent[]>([]);
  const seen = useRef(new Map<string, Seen>());

  useEffect(() => {
    const fresh: RunEvent[] = [];

    for (const job of jobs) {
      const snapshot = live.get(job.id) ?? null;
      const now = seenOf(snapshot);
      if (!now) continue;
      const before = seen.current.get(job.id);
      seen.current.set(job.id, now);
      // First sight: remember, announce nothing.
      if (!before) continue;

      const who = nameFor(job);
      const at = snapshot!.status.observed.atUnixSeconds
        ?? Math.floor(snapshot!.updatedAtMillis / 1000);

      // Doors that changed hands since the last look. `outcomes` is
      // cumulative and one entry per plan stop, so a changed index is a
      // door the run has newly said something about.
      now.outcomes.forEach((outcome, index) => {
        const was = before.outcomes[index] ?? STOP_OUTCOME.PENDING;
        if (outcome === was || outcome === STOP_OUTCOME.PENDING) return;
        const label = job.stops[index]?.label ?? `Stop ${index + 1}`;
        if (outcome === STOP_OUTCOME.DELIVERED) {
          fresh.push({
            id: `${job.id}-${index}-${outcome}`,
            jobId: job.id, atUnixSeconds: at, who,
            what: `delivered ${label}`, tone: "settled"
          });
        } else {
          fresh.push({
            id: `${job.id}-${index}-${outcome}`,
            jobId: job.id, atUnixSeconds: at, who,
            what: outcome === STOP_OUTCOME.SKIPPED
              ? `skipped ${label}`
              : `could not deliver ${label}`,
            tone: "halt"
          });
        }
      });

      // Went quiet. The shape of a phone that died in a basement, and
      // the thing a dispatcher otherwise finds out about too late.
      if (now.stale && !before.stale) {
        fresh.push({
          id: `${job.id}-quiet-${at}`,
          jobId: job.id, atUnixSeconds: at, who,
          what: "has gone quiet", tone: "quiet"
        });
      }
      // And came back.
      if (!now.stale && before.stale) {
        fresh.push({
          id: `${job.id}-back-${at}`,
          jobId: job.id, atUnixSeconds: at, who,
          what: "is publishing again", tone: "live"
        });
      }
      // Started publishing for the first time — the moment a job stops
      // being a piece of paper and becomes a run.
      if (now.ever && !before.ever) {
        fresh.push({
          id: `${job.id}-took-${at}`,
          jobId: job.id, atUnixSeconds: at, who,
          what: "took the job", tone: "live"
        });
      }
      if (now.ended && !before.ended) {
        fresh.push({
          id: `${job.id}-done-${at}`,
          jobId: job.id, atUnixSeconds: at, who,
          what: "finished the round", tone: "settled"
        });
      }
    }

    if (!fresh.length) return;
    setEvents(current => {
      const byId = new Map(current.map(event => [event.id, event]));
      for (const event of fresh) byId.set(event.id, event);
      return [...byId.values()]
        .sort((a, b) => b.atUnixSeconds - a.atUnixSeconds)
        .slice(0, KEEP);
    });
  }, [jobs, live, nameFor]);

  return events;
}
